/**
 * <s-logo-upload> shows the front and/or back logo upload fields that match the
 * selected print placement option. Fields that don't apply are hidden and disabled,
 * so they are neither required nor submitted with the product form.
 *
 * Attributes:
 *   data-option-name — the product option that controls placement (e.g. "Print placement").
 * Children:
 *   [data-logo-side="front"] and [data-logo-side="back"] wrappers, each holding one file input.
 */
class SLogoUpload extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const host = this.closest('s-product-form') || document;

    host.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
      if (target.name !== this.dataset.optionName) return;
      this.#apply(target.value);
    }, { signal: this.#ac.signal });

    this.#apply(this.#currentValue(host));
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #currentValue(host) {
    const name = this.dataset.optionName;
    if (!name) return '';
    const controls = host.querySelectorAll(`[name="${CSS.escape(name)}"]`);
    for (const control of controls) {
      if (control instanceof HTMLSelectElement) return control.value;
      if (control instanceof HTMLInputElement && control.checked) return control.value;
    }
    return '';
  }

  #apply(value) {
    const placement = String(value || '').toLowerCase();
    // Without a placement option, only the front field is offered.
    // "Both sides" (e.g. tote bags) offers front and back; "One side" offers front only.
    const both = placement.includes('both');
    const needsFront = !placement || both || placement.includes('front') || placement.includes('one side');
    const needsBack = both || placement.includes('back');

    this.#toggle('front', needsFront);
    this.#toggle('back', needsBack);
  }

  #toggle(side, enabled) {
    const wrapper = this.querySelector(`[data-logo-side="${side}"]`);
    if (!wrapper) return;
    const input = wrapper.querySelector('input[type="file"]');

    wrapper.hidden = !enabled;
    if (input instanceof HTMLInputElement) {
      input.disabled = !enabled;
      input.required = enabled;
    }
  }
}

if (!customElements.get('s-logo-upload')) {
  customElements.define('s-logo-upload', SLogoUpload);
}
